import ApiService from "@/core/services/ApiService"
import { Actions, Mutations } from "@/store/enums/StoreEnums"
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators"

export interface OperatingCost {
  id: number
  category: string
  amount: number
  cost_date: string
  note: string | null
  linkable_type: string | null
  linkable_id: number | null
  extra: Record<string, unknown> | null
  created_by: number | null
  created_at: string
  updated_at: string
}

export interface OperatingCostMonthlySummary {
  from: string
  to: string
  total_cost: number
  by_category: Record<string, number>
  profit_sum: number
  net_profit: number
}

@Module
export default class OperatingCostModule extends VuexModule {
  operating_costs = [] as Array<OperatingCost>
  operating_cost_count = 0
  operating_cost_total_amount = 0
  operating_cost = {} as OperatingCost
  operating_cost_monthly_summary = {} as OperatingCostMonthlySummary

  get currentOperatingCosts(): Array<OperatingCost> {
    return this.operating_costs
  }

  get currentOperatingCostCount() {
    return this.operating_cost_count
  }

  get currentOperatingCostTotalAmount() {
    return this.operating_cost_total_amount
  }

  get currentOperatingCost(): OperatingCost {
    return this.operating_cost
  }

  get currentOperatingCostMonthlySummary(): OperatingCostMonthlySummary {
    return this.operating_cost_monthly_summary
  }

  @Mutation
  [Mutations.SET_OPERATING_COSTS](items) {
    this.operating_costs = items
  }

  @Mutation
  [Mutations.SET_OPERATING_COST_COUNT](count) {
    this.operating_cost_count = count
  }

  @Mutation
  [Mutations.SET_OPERATING_COST_TOTAL_AMOUNT](amount) {
    this.operating_cost_total_amount = amount
  }

  @Mutation
  [Mutations.SET_OPERATING_COST](item) {
    this.operating_cost = item
  }

  @Mutation
  [Mutations.SET_OPERATING_COST_MONTHLY_SUMMARY](summary) {
    this.operating_cost_monthly_summary = summary
  }

  @Action
  [Actions.GET_OPERATING_COSTS](params) {
    ApiService.setHeader()
    let qs = "?"
    if (params) {
      Object.keys(params).forEach((k) => {
        if (params[k] !== undefined && params[k] !== null && params[k] !== "") {
          qs += "&" + k + "=" + encodeURIComponent(params[k])
        }
      })
    }
    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/operating_costs", qs)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_OPERATING_COSTS, data["items"] || [])
          this.context.commit(Mutations.SET_OPERATING_COST_COUNT, data["total"] || 0)
          this.context.commit(Mutations.SET_OPERATING_COST_TOTAL_AMOUNT, data["total_amount"] || 0)
          resolve()
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors)
          reject()
        })
    })
  }

  @Action
  [Actions.CREATE_OPERATING_COST](params) {
    ApiService.setHeader()
    return new Promise<void>((resolve, reject) => {
      ApiService.post("admin/operating_costs", { operating_cost: params })
        .then(({ data }) => {
          if (!data.success) {
            this.context.commit(Mutations.SET_ERROR, data.error_msg)
            reject(new Error(data.error_msg))
            return
          }
          this.context.commit(Mutations.SET_OPERATING_COST, data["item"])
          resolve()
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.error_msg || response?.data?.errors)
          reject(response?.data)
        })
    })
  }

  @Action
  [Actions.UPDATE_OPERATING_COST](params) {
    ApiService.setHeader()
    const id = params.id
    return new Promise<void>((resolve, reject) => {
      ApiService.put(`admin/operating_costs/${id}`, { operating_cost: params })
        .then(({ data }) => {
          if (!data.success) {
            this.context.commit(Mutations.SET_ERROR, data.error_msg)
            reject(new Error(data.error_msg))
            return
          }
          this.context.commit(Mutations.SET_OPERATING_COST, data["item"])
          resolve()
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.error_msg || response?.data?.errors)
          reject(response?.data)
        })
    })
  }

  @Action
  [Actions.DELETE_OPERATING_COST](id) {
    ApiService.setHeader()
    return new Promise<void>((resolve, reject) => {
      ApiService.delete(`admin/operating_costs/${id}`)
        .then(({ data }) => {
          if (!data.success) {
            this.context.commit(Mutations.SET_ERROR, data.error_msg)
            reject()
            return
          }
          resolve()
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors)
          reject()
        })
    })
  }

  @Action
  [Actions.GET_OPERATING_COST_MONTHLY_SUMMARY](params) {
    ApiService.setHeader()
    let qs = "?"
    if (params) {
      Object.keys(params).forEach((k) => {
        if (params[k] !== undefined && params[k] !== null && params[k] !== "") {
          qs += "&" + k + "=" + encodeURIComponent(params[k])
        }
      })
    }
    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/operating_costs/monthly_summary", qs)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_OPERATING_COST_MONTHLY_SUMMARY, data)
          resolve()
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors)
          reject()
        })
    })
  }
}
